<template>
  <div v-if="stripe">
    <subscription-card-element
      v-show="paymentMethod === 'card'"
      v-bind="{stripe, isSettingsStyle, isLoading}"
      @complete="tokenizeElement"
      @changePaymentMethod="paymentMethod = null"
    />

    <subscription-iban-element
      v-show="paymentMethod === 'iban'"
      v-bind="{stripe, isSettingsStyle, isLoading}"
      @complete="tokenizeElement"
      @changePaymentMethod="paymentMethod = null"
    />

    <v-card
      v-show="!paymentMethod"
      :flat="isSettingsStyle">
      <v-list two-line>
        <template v-for="(item, index) in items">
          <v-subheader
            v-if="item.header"
            :key="item.header"
          >
            {{ item.header }}
          </v-subheader>

          <v-divider
            v-else-if="item.divider"
            :key="index"
          />

          <v-list-tile
            v-else
            :key="item.title"
            avatar
            @click="paymentMethod = item.paymentMethod"
          >
            <v-list-tile-content>
              <v-list-tile-title> {{ item.title }}</v-list-tile-title>
              <v-list-tile-sub-title>{{ item.subtitle }}</v-list-tile-sub-title>
            </v-list-tile-content>
            <v-list-tile-action>
              <v-icon color="grey lighten-1">expand_more</v-icon>
            </v-list-tile-action>
          </v-list-tile>
        </template>
      </v-list>
      <v-card-actions v-if="!isSettingsStyle">
        <v-spacer/>
        <v-btn
          flat
          @click="$emit('prev')"
        >
          {{ $t('prev') }}
        </v-btn>
      </v-card-actions>
    </v-card>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import SubscriptionCardElement from './SubscriptionCardElement'
import SubscriptionIbanElement from './SubscriptionIbanElement'

export default {
  name: 'SubscriptionPaymentMethod',
  components: { SubscriptionIbanElement, SubscriptionCardElement },
  props: ['isSettingsStyle', 'isLoading'],
  data () {
    return {
      stripe: null,
      paymentMethod: null,
      items: [
        {
          title: this.$t('creditCard'),
          subtitle: this.$t('creditCardExplanation'),
          paymentMethod: 'card'
        },
        { divider: true, inset: true },
        {
          title: this.$t('iban'),
          subtitle: this.$t('ibanExplanation'),
          paymentMethod: 'iban'
        }
      ]
    }
  },
  computed: {
    ...mapGetters('user', ['getUser'])
  },
  methods: {
    async tokenizeElement ({ element, data }) {
      if (this.paymentMethod === 'card') {
        this.makeCardPayment(element, data)
      }
      if (this.paymentMethod === 'iban') {
        this.makeIbanPayment(element, data)
      }
    },
    async makeCardPayment (element, data) {
      try {
        this.$emit('loading', true)
        const [{ error, token }] = await Promise.all([
          this.stripe.createToken(element, data),
        ])

        this.$emit('loading', false)
        if (error) {
          this.onStripeError(error.message)
        } else {
          this.$emit('stripeTokenGenerated', token)
          this.$emit('next')
          return token
        }
      } catch (e) {
        this.onStripeError(e.message)
      }
      this.$emit('loading', false)
    },
    async makeIbanPayment (element, data) {
      try {
        const sourceData = {
          type: 'sepa_debit',
          currency: 'eur',
          owner: {
            name: data.account_holder_name
          }
        }

        this.$emit('loading', true)
        const [{ error, source }] = await Promise.all([
          this.stripe.createSource(element, sourceData),
        ])

        this.$emit('loading', false)
        if (error) {
          this.onStripeError(error.message)
        } else {
          console.log(source)

          this.$emit('stripeTokenGenerated', source)
          this.$emit('next')
          return source
        }
      } catch (e) {
        this.onStripeError(e.message)
      }
      this.$emit('loading', false)
    },
    onStripeError (error) {
      this.$notification({
        errors: {
          message: [error]
        }
      })
    },
    stripeInit () {
      this.stripe = window.Stripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
    },
    stripeLoad () {
      if (this.isStripeLoaded()) return this.stripeInit()

      const stripeScript = document.createElement('script')

      stripeScript.src = process.env.VUE_APP_STRIPE_LIB
      stripeScript.type = 'text/javascript'
      stripeScript.onload = () => this.stripeInit()
      stripeScript.onerror = () => this.$notification({
        errors: {
          message: [this.$t('stripeLibCanNotBeLoaded')]
        }
      })

      document.head.appendChild(stripeScript)
    },
    isStripeLoaded () {
      const scripts = document.getElementsByTagName('script')
      return [...scripts].some(script => script.src === process.env.VUE_APP_STRIPE_LIB)
    }
  },
  created () {
    this.stripeLoad()
  },
  i18n: {
    messages: {
      de: {
        creditCard: 'Kreditkarte',
        creditCardExplanation: 'Visa, MasterCard, American Express, JCB, Discover, und Diners Club',
        iban: 'SEPA Lastschrift',
        ibanExplanation: 'Automatischer Lastschrifteinzug auf ihr Konto',
        prev: 'Zurück',
        stripeLibCanNotBeLoaded: 'Es ist ein Fehler mit der Zahlungsverarbeitung aufgetreten'
      },
      en: {
        creditCard: 'credit card',
        creditCardExplanation: 'Visa, MasterCard, American Express, JCB, Discover, and Diners Club',
        iban: 'SEPA direct debit',
        ibanExplanation: 'Automatic direct debit to your account',
        prev: 'Back',
        stripeLibCanNotBeLoaded: 'An error has occurred with the payment processing'
      },
      tr: {
        creditCard: 'kredi kartı',
        creditCardExplanation: 'Visa, MasterCard, American Express, JCB, Discover ve Diners Club',
        iban: 'SEPA doğrudan para transferi',
        ibanExplanation: 'Hesabınıza doğrudan otomatik para transferi',
        prev: 'Geri',
        stripeLibCanNotBeLoaded: 'Ödeme işleminde bir hata oluştu'
      },
      ru: {
        creditCard: 'Кредитная карта',
        creditCardExplanation: 'Visa, MasterCard, American Express, JCB, Discover, и Diners Club',
        iban: 'SEPA прямой дебет',
        ibanExplanation: 'Автоматический прямой дебет на ваш счет',
        prev: 'Назад',
        stripeLibCanNotBeLoaded: 'Произошла ошибка при обработке платежа'
      }
    }
  }
}
</script>

<style>

  .StripeElementGroup {
    position: relative;
  }

  .StripeElement {
    font-size: 18px;
    padding: 10px 10px 10px 5px;
    display: block;
    width: 100%;
    border-bottom: 1px solid rgba(0, 0, 0, 0.42);
    transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  }

  .StripeElement:hover {
    border-bottom: 1px solid rgba(0, 0, 0, 0.87);
  }

  .StripeElement:focus {
    outline: none;
  }

  .bar {
    position: relative;
    display: block;
    width: 100%;
    height: 1px;
  }

  .bar:before, .bar:after {
    content: '';
    height: 2px;
    width: 0;
    bottom: 1px;
    position: absolute;
    background: rgb(25, 118, 210);
    -webkit-transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
    transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  }

  .bar:before {
    left: 50%;
  }

  .bar:after {
    right: 50%;
  }

  .StripeElement--focus ~ .bar:before,
  .StripeElement--focus ~ .bar:after {
    width: 50%;
  }

  .highlight {
    position: absolute;
    height: 60%;
    width: 100px;
    top: 25%;
    left: 0;
    pointer-events: none;
    opacity: 0.5;
  }
</style>
